import useCreateLog from '@/log/hooks/useCreateLog'
import { useOrgContext } from '@/org/contexts/OrgContext'
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogProps,
  Button,
  ListItem,
  Text,
  UnorderedList,
} from '@chakra-ui/react'
import { RoleSummaryFragment, useArchiveRoleMutation } from '@gql'
import { EntityChangeType, LogType } from '@rolebase/shared/model/log'
import React, { useMemo, useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'

interface Props extends Omit<
  AlertDialogProps,
  'children' | 'leastDestructiveRef'
> {
  role: RoleSummaryFragment
  onDelete?(): void
}

export default function RoleDeleteModal({
  role,
  onDelete,
  ...alertProps
}: Props) {
  const { t } = useTranslation()
  const [archiveRole] = useArchiveRoleMutation()
  const createLog = useCreateLog()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const { orgData } = useOrgContext()

  // A base role still carried by circles cannot be deleted: archiving it alone
  // left those circles pointing at a role nobody can read, drawn empty in the
  // org chart and impossible to open or remove (#94, 25/09/2026). Named by
  // their parent circle, since they all bear the base role's name.
  const carriers = useMemo(
    () =>
      role.base
        ? (orgData?.circles ?? [])
            .filter((circle) => circle.roleId === role.id)
            .map(
              (circle) =>
                orgData?.getRole(
                  orgData.getCircle(circle.parentId ?? undefined)?.roleId
                )?.name ?? '?'
            )
        : [],
    [orgData, role]
  )
  const isUsed = carriers.length > 0

  const handleDelete = async () => {
    if (!role || isUsed) return
    const { data } = await archiveRole({
      variables: { id: role.id, archivedAt: new Date().toISOString() },
    })
    onDelete?.()
    alertProps.onClose()

    // Use the DB-returned value (timestamptz "...+00:00"), not the "...Z" input,
    // so "data changed since" comparisons stay accurate.
    const archivedAt = data?.update_role_by_pk?.archivedAt
    if (!archivedAt) return

    // Log change
    createLog({
      display: {
        type: LogType.RoleArchive,
        id: role.id,
        name: role.name,
      },
      changes: {
        roles: [
          {
            type: EntityChangeType.Update,
            id: role.id,
            prevData: { archivedAt: null },
            newData: { archivedAt },
          },
        ],
      },
    })
  }

  if (!role) return null

  return (
    <AlertDialog {...alertProps} leastDestructiveRef={cancelRef}>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>{t('RoleDeleteModal.heading')}</AlertDialogHeader>

          <AlertDialogBody>
            {isUsed ? (
              <>
                <Text>
                  <Trans
                    i18nKey="RoleDeleteModal.usedBy"
                    values={{ name: role.name, count: carriers.length }}
                    components={{ b: <strong /> }}
                  />
                </Text>
                <UnorderedList mt={2} ml={6}>
                  {carriers.map((parentName, index) => (
                    <ListItem key={index}>{parentName}</ListItem>
                  ))}
                </UnorderedList>
              </>
            ) : (
              <Text>
                <Trans
                  i18nKey="RoleDeleteModal.info"
                  values={{ name: role.name }}
                  components={{ b: <strong /> }}
                />
              </Text>
            )}
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={alertProps.onClose}>
              {isUsed ? t('common.close') : t('common.cancel')}
            </Button>
            {!isUsed && (
              <Button colorScheme="red" onClick={handleDelete} ml={3}>
                {t('common.delete')}
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}
